import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import { Edit, Eye, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import type { ClientResponseDTO } from "../types/client.types";
import { Can } from "@/features/auth/components/Can";
import { ACTIONS } from "@/constants/roles";
import { memo } from "react";
import {
  formatDocument,
  formatPhone,
  formatCEP,
} from "@/shared/lib/formatters";
import { PersonType } from "@/shared/types/enums";

interface ClientsTableProps {
  clients: ClientResponseDTO[];
  loading: boolean;
  currentPage: number;
  totalPages: number;
  onView: (client: ClientResponseDTO) => void;
  onEdit: (client: ClientResponseDTO) => void;
  onDelete: (client: ClientResponseDTO) => void;
  onPageChange: (page: number) => void;
}

export const ClientsTable = memo(function ClientsTable({
  clients,
  loading,
  currentPage,
  totalPages,
  onView,
  onEdit,
  onDelete,
  onPageChange,
}: ClientsTableProps) {
  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nome / Razão Social</TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead>Documento</TableHead>
            <TableHead>Telefone</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Endereço</TableHead>
            <TableHead>Gênero</TableHead>
            <TableHead className="text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {clients.map((client) => (
            <TableRow key={client.id}>
              <TableCell className="font-medium">{client.name}</TableCell>
              <TableCell>
                <Badge variant="secondary">
                  {client.personType === PersonType.JURIDICA ? "PJ" : "PF"}
                </Badge>
              </TableCell>
              <TableCell>{formatDocument(client.document)}</TableCell>
              <TableCell>
                {client.phone.length >= 10
                  ? formatPhone(client.phone)
                  : client.phone}
              </TableCell>
              <TableCell className="text-muted-foreground">
                {client.email || "-"}
              </TableCell>
              <TableCell className="text-muted-foreground max-w-xs truncate">
                {client.street}, {client.number} - {client.neighborhood},{" "}
                {client.state} - CEP:{" "}
                {client.postalCode.length === 8
                  ? formatCEP(client.postalCode)
                  : client.postalCode}
              </TableCell>
              <TableCell>
                {client.gender ? (
                  <Badge variant="outline">
                    {client.gender === "M" ? "Masculino" : "Feminino"}
                  </Badge>
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  <Can permission={ACTIONS.clients.read}>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => onView(client)}
                      title="Visualizar"
                      disabled={loading}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </Can>
                  <Can permission={ACTIONS.clients.update}>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => onEdit(client)}
                      title="Editar"
                      disabled={loading}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                  </Can>
                  <Can permission={ACTIONS.clients.delete}>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => onDelete(client)}
                      title="Excluir"
                      disabled={loading}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </Can>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex flex-col md:flex-row items-center justify-between mt-4 pt-4 border-t gap-4">
          <div className="text-sm text-muted-foreground">
            Página {currentPage + 1} de {totalPages}
          </div>
          <div className="flex gap-2 items-center flex-wrap justify-center">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 0 || loading}
            >
              <ChevronLeft className="w-4 h-4" />
              Anterior
            </Button>

            {/* Páginas */}
            <div className="flex gap-1 flex-wrap justify-center">
              {Array.from({ length: totalPages }, (_, i) => {
                const showPage =
                  i === 0 ||
                  i === totalPages - 1 ||
                  (i >= currentPage - 1 && i <= currentPage + 1);

                const showEllipsis =
                  (i === 1 && currentPage > 2) ||
                  (i === totalPages - 2 && currentPage < totalPages - 3);

                if (showEllipsis) {
                  return (
                    <span
                      key={i}
                      className="px-2 py-1 text-sm text-muted-foreground"
                    >
                      ...
                    </span>
                  );
                }

                if (!showPage) return null;

                return (
                  <Button
                    key={i}
                    variant={currentPage === i ? "default" : "outline"}
                    size="sm"
                    onClick={() => onPageChange(i)}
                    disabled={loading}
                    className="min-w-10"
                  >
                    {i + 1}
                  </Button>
                );
              })}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages - 1 || loading}
            >
              Próxima
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
});
